import React from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { Modal, ModalProps, ModalHeader, ModalBody, ModalFooter } from './Modal';
import { BottomSheet, BottomSheetHeader, BottomSheetBody, BottomSheetFooter } from './BottomSheet';

interface ResponsiveModalProps extends Omit<ModalProps, 'mobileFullScreen'> {
  snapPoints?: number[];
  defaultSnap?: number;
  showHandle?: boolean;
}

interface ResponsiveModalHeaderProps {
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

interface ResponsiveModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface ResponsiveModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * ResponsiveModal automatically uses BottomSheet on mobile and Modal on desktop
 * Provides a seamless API that works across all screen sizes
 */
export function ResponsiveModal({
  isOpen,
  onClose,
  children,
  className,
  size = 'md',
  snapPoints = [0.5, 0.9],
  defaultSnap = 1,
  showHandle = true,
  ...props
}: ResponsiveModalProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        className={className}
        snapPoints={snapPoints}
        defaultSnap={defaultSnap}
        showHandle={showHandle}
        {...props}
      >
        {children}
      </BottomSheet>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
      size={size}
      mobileFullScreen={false}
      {...props}
    >
      {children}
    </Modal>
  );
}

export function ResponsiveModalHeader({
  children,
  onClose,
  className,
}: ResponsiveModalHeaderProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <BottomSheetHeader onClose={onClose} className={className}>
        {children}
      </BottomSheetHeader>
    );
  }

  return (
    <ModalHeader onClose={onClose} className={className}>
      {children}
    </ModalHeader>
  );
}

export function ResponsiveModalBody({ children, className }: ResponsiveModalBodyProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <BottomSheetBody className={className}>{children}</BottomSheetBody>;
  }

  return <ModalBody className={className}>{children}</ModalBody>;
}

export function ResponsiveModalFooter({ children, className }: ResponsiveModalFooterProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <BottomSheetFooter className={className}>{children}</BottomSheetFooter>;
  }

  return <ModalFooter className={className}>{children}</ModalFooter>;
}
